import { r as registerInstance, c as createEvent, h, H as Host, g as getElement } from './index-8f7fb048.js';
import { a as getTextInputAriaLabel } from './aria-f37957bf.js';
import { s as submitFormImplicitly } from './forms-eb6a5fcc.js';
import { g as getNamespacedTagFor } from './index-739b155e.js';
import { c as classNames } from './classnames-ba9ba5b9.js';
import { g as getMaxZIndex } from './max-z-index-7a248b89.js';

const sides = ['top', 'right', 'bottom', 'left'];
const alignments = ['start', 'end'];
const placements = /*#__PURE__*/sides.reduce((acc, side) => acc.concat(side, side + "-" + alignments[0], side + "-" + alignments[1]), []);
const min = Math.min;
const max = Math.max;
const round = Math.round;
const floor = Math.floor;
const createCoords = v => ({
  x: v,
  y: v
});
const oppositeSideMap = {
  left: 'right',
  right: 'left',
  bottom: 'top',
  top: 'bottom'
};
const oppositeAlignmentMap = {
  start: 'end',
  end: 'start'
};
function evaluate(value, param) {
  return typeof value === 'function' ? value(param) : value;
}
function getSide(placement) {
  return placement.split('-')[0];
}
function getAlignment(placement) {
  return placement.split('-')[1];
}
function getOppositeAxis(axis) {
  return axis === 'x' ? 'y' : 'x';
}
function getAxisLength(axis) {
  return axis === 'y' ? 'height' : 'width';
}
function getSideAxis(placement) {
  return ['top', 'bottom'].includes(getSide(placement)) ? 'y' : 'x';
}
function getAlignmentAxis(placement) {
  return getOppositeAxis(getSideAxis(placement));
}
function getAlignmentSides(placement, rects, rtl) {
  if (rtl === void 0) {
    rtl = false;
  }
  const alignment = getAlignment(placement);
  const alignmentAxis = getAlignmentAxis(placement);
  const length = getAxisLength(alignmentAxis);
  let mainAlignmentSide = alignmentAxis === 'x' ? alignment === (rtl ? 'end' : 'start') ? 'right' : 'left' : alignment === 'start' ? 'bottom' : 'top';
  if (rects.reference[length] > rects.floating[length]) {
    mainAlignmentSide = getOppositePlacement(mainAlignmentSide);
  }
  return [mainAlignmentSide, getOppositePlacement(mainAlignmentSide)];
}
function getOppositeAlignmentPlacement(placement) {
  return placement.replace(/start|end/g, alignment => oppositeAlignmentMap[alignment]);
}
function getOppositePlacement(placement) {
  return placement.replace(/left|right|bottom|top/g, side => oppositeSideMap[side]);
}
function expandPaddingObject(padding) {
  return {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    ...padding
  };
}
function getPaddingObject(padding) {
  return typeof padding !== 'number' ? expandPaddingObject(padding) : {
    top: padding,
    right: padding,
    bottom: padding,
    left: padding
  };
}
function rectToClientRect(rect) {
  return {
    ...rect,
    top: rect.y,
    left: rect.x,
    right: rect.x + rect.width,
    bottom: rect.y + rect.height
  };
}

function computeCoordsFromPlacement(_ref, placement, rtl) {
  let {
    reference,
    floating
  } = _ref;
  const sideAxis = getSideAxis(placement);
  const alignmentAxis = getAlignmentAxis(placement);
  const alignLength = getAxisLength(alignmentAxis);
  const side = getSide(placement);
  const isVertical = sideAxis === 'y';
  const commonX = reference.x + reference.width / 2 - floating.width / 2;
  const commonY = reference.y + reference.height / 2 - floating.height / 2;
  const commonAlign = reference[alignLength] / 2 - floating[alignLength] / 2;
  let coords;
  switch (side) {
    case 'top':
      coords = {
        x: commonX,
        y: reference.y - floating.height
      };
      break;
    case 'bottom':
      coords = {
        x: commonX,
        y: reference.y + reference.height
      };
      break;
    case 'right':
      coords = {
        x: reference.x + reference.width,
        y: commonY
      };
      break;
    case 'left':
      coords = {
        x: reference.x - floating.width,
        y: commonY
      };
      break;
    default:
      coords = {
        x: reference.x,
        y: reference.y
      };
  }
  switch (getAlignment(placement)) {
    case 'start':
      coords[alignmentAxis] -= commonAlign * (rtl && isVertical ? -1 : 1);
      break;
    case 'end':
      coords[alignmentAxis] += commonAlign * (rtl && isVertical ? -1 : 1);
      break;
  }
  return coords;
}

/**
 * Computes the `x` and `y` coordinates that will place the floating element
 * next to a reference element when it is given a certain positioning strategy.
 *
 * This export does not have any `platform` interface logic. You will need to
 * write one for the platform you are using Floating UI with.
 */
const computePosition$1 = async (reference, floating, config) => {
  const {
    placement = 'bottom',
    strategy = 'absolute',
    middleware = [],
    platform
  } = config;
  const validMiddleware = middleware.filter(Boolean);
  const rtl = await (platform.isRTL == null ? void 0 : platform.isRTL(floating));
  let rects = await platform.getElementRects({
    reference,
    floating,
    strategy
  });
  let {
    x,
    y
  } = computeCoordsFromPlacement(rects, placement, rtl);
  let statefulPlacement = placement;
  let middlewareData = {};
  let resetCount = 0;
  for (let i = 0; i < validMiddleware.length; i++) {
    const {
      name,
      fn
    } = validMiddleware[i];
    const {
      x: nextX,
      y: nextY,
      data,
      reset
    } = await fn({
      x,
      y,
      initialPlacement: placement,
      placement: statefulPlacement,
      strategy,
      middlewareData,
      rects,
      platform,
      elements: {
        reference,
        floating
      }
    });
    x = nextX != null ? nextX : x;
    y = nextY != null ? nextY : y;
    middlewareData = {
      ...middlewareData,
      [name]: {
        ...middlewareData[name],
        ...data
      }
    };
    if (reset && resetCount <= 50) {
      resetCount++;
      if (typeof reset === 'object') {
        if (reset.placement) {
          statefulPlacement = reset.placement;
        }
        if (reset.rects) {
          rects = reset.rects === true ? await platform.getElementRects({
            reference,
            floating,
            strategy
          }) : reset.rects;
        }
        ({
          x,
          y
        } = computeCoordsFromPlacement(rects, statefulPlacement, rtl));
      }
      i = -1;
      continue;
    }
  }
  return {
    x,
    y,
    placement: statefulPlacement,
    strategy,
    middlewareData
  };
};

/**
 * Resolves with an object of overflow side offsets that determine how much the
 * element is overflowing a given clipping boundary on each side.
 * - positive = overflowing the boundary by that number of pixels
 * - negative = how many pixels left before it will overflow
 * - 0 = lies flush with the boundary
 * @see https://floating-ui.com/docs/detectOverflow
 */
async function detectOverflow(state, options) {
  var _await$platform$isEle;
  if (options === void 0) {
    options = {};
  }
  const {
    x,
    y,
    platform,
    rects,
    elements,
    strategy
  } = state;
  const {
    boundary = 'clippingAncestors',
    rootBoundary = 'viewport',
    elementContext = 'floating',
    altBoundary = false,
    padding = 0
  } = evaluate(options, state);
  const paddingObject = getPaddingObject(padding);
  const altContext = elementContext === 'floating' ? 'reference' : 'floating';
  const element = elements[altBoundary ? altContext : elementContext];
  const clippingClientRect = rectToClientRect(await platform.getClippingRect({
    element: ((_await$platform$isEle = await (platform.isElement == null ? void 0 : platform.isElement(element))) != null ? _await$platform$isEle : true) ? element : element.contextElement || (await (platform.getDocumentElement == null ? void 0 : platform.getDocumentElement(elements.floating))),
    boundary,
    rootBoundary,
    strategy
  }));
  const rect = elementContext === 'floating' ? {
    ...rects.floating,
    x,
    y
  } : rects.reference;
  const offsetParent = await (platform.getOffsetParent == null ? void 0 : platform.getOffsetParent(elements.floating));
  const offsetScale = (await (platform.isElement == null ? void 0 : platform.isElement(offsetParent))) ? (await (platform.getScale == null ? void 0 : platform.getScale(offsetParent))) || {
    x: 1,
    y: 1
  } : {
    x: 1,
    y: 1
  };
  const elementClientRect = rectToClientRect(platform.convertOffsetParentRelativeRectToViewportRelativeRect ? await platform.convertOffsetParentRelativeRectToViewportRelativeRect({
    rect,
    offsetParent,
    strategy
  }) : rect);
  return {
    top: (clippingClientRect.top - elementClientRect.top + paddingObject.top) / offsetScale.y,
    bottom: (elementClientRect.bottom - clippingClientRect.bottom + paddingObject.bottom) / offsetScale.y,
    left: (clippingClientRect.left - elementClientRect.left + paddingObject.left) / offsetScale.x,
    right: (elementClientRect.right - clippingClientRect.right + paddingObject.right) / offsetScale.x
  };
}

function getPlacementList(alignment, autoAlignment, allowedPlacements) {
  const allowedPlacementsSortedByAlignment = alignment ? [...allowedPlacements.filter(placement => getAlignment(placement) === alignment), ...allowedPlacements.filter(placement => getAlignment(placement) !== alignment)] : allowedPlacements.filter(placement => getSide(placement) === placement);
  return allowedPlacementsSortedByAlignment.filter(placement => {
    if (alignment) {
      return getAlignment(placement) === alignment || (autoAlignment ? getOppositeAlignmentPlacement(placement) !== placement : false);
    }
    return true;
  });
}
/**
 * Optimizes the visibility of the floating element by choosing the placement
 * that has the most space available automatically, without needing to specify a
 * preferred placement. Alternative to `flip`.
 * @see https://floating-ui.com/docs/autoPlacement
 */
const autoPlacement = function (options) {
  if (options === void 0) {
    options = {};
  }
  return {
    name: 'autoPlacement',
    options,
    async fn(state) {
      var _middlewareData$autoP, _middlewareData$autoP2, _placementsThatFitOnE;
      const {
        rects,
        middlewareData,
        placement,
        platform,
        elements
      } = state;
      const {
        crossAxis = false,
        alignment,
        allowedPlacements = placements,
        autoAlignment = true,
        ...detectOverflowOptions
      } = evaluate(options, state);
      const placements$1 = alignment !== undefined || allowedPlacements === placements ? getPlacementList(alignment || null, autoAlignment, allowedPlacements) : allowedPlacements;
      const overflow = await detectOverflow(state, detectOverflowOptions);
      const currentIndex = ((_middlewareData$autoP = middlewareData.autoPlacement) == null ? void 0 : _middlewareData$autoP.index) || 0;
      const currentPlacement = placements$1[currentIndex];
      if (currentPlacement == null) {
        return {};
      }
      const alignmentSides = getAlignmentSides(currentPlacement, rects, await (platform.isRTL == null ? void 0 : platform.isRTL(elements.floating)));

      // Make `computeCoords` start from the right place.
      if (placement !== currentPlacement) {
        return {
          reset: {
            placement: placements$1[0]
          }
        };
      }
      const currentOverflows = [overflow[getSide(currentPlacement)], overflow[alignmentSides[0]], overflow[alignmentSides[1]]];
      const allOverflows = [...(((_middlewareData$autoP2 = middlewareData.autoPlacement) == null ? void 0 : _middlewareData$autoP2.overflows) || []), {
        placement: currentPlacement,
        overflows: currentOverflows
      }];
      const nextPlacement = placements$1[currentIndex + 1];

      // There are more placements to check.
      if (nextPlacement) {
        return {
          data: {
            index: currentIndex + 1,
            overflows: allOverflows
          },
          reset: {
            placement: nextPlacement
          }
        };
      }
      const placementsSortedByMostSpace = allOverflows.map(d => {
        const alignment = getAlignment(d.placement);
        return [d.placement, alignment && crossAxis ?
        // Check along the mainAxis and main crossAxis side.
        d.overflows.slice(0, 2).reduce((acc, v) => acc + v, 0) :
        // Check only the mainAxis.
        d.overflows[0], d.overflows];
      }).sort((a, b) => a[1] - b[1]);
      const placementsThatFitOnEachSide = placementsSortedByMostSpace.filter(d => d[2].slice(0,
      // Aligned placements should not check their opposite crossAxis
      // side.
      getAlignment(d[0]) ? 2 : 3).every(v => v <= 0));
      const resetPlacement = ((_placementsThatFitOnE = placementsThatFitOnEachSide[0]) == null ? void 0 : _placementsThatFitOnE[0]) || placementsSortedByMostSpace[0][0];
      if (resetPlacement !== placement) {
        return {
          data: {
            index: currentIndex + 1,
            overflows: allOverflows
          },
          reset: {
            placement: resetPlacement
          }
        };
      }
      return {};
    }
  };
};

// For type backwards-compatibility, the `OffsetOptions` type was also
// Derivable.
async function convertValueToCoords(state, options) {
  const {
    placement,
    platform,
    elements
  } = state;
  const rtl = await (platform.isRTL == null ? void 0 : platform.isRTL(elements.floating));
  const side = getSide(placement);
  const alignment = getAlignment(placement);
  const isVertical = getSideAxis(placement) === 'y';
  const mainAxisMulti = ['left', 'top'].includes(side) ? -1 : 1;
  const crossAxisMulti = rtl && isVertical ? -1 : 1;
  const rawValue = evaluate(options, state);

  // eslint-disable-next-line prefer-const
  let {
    mainAxis,
    crossAxis,
    alignmentAxis
  } = typeof rawValue === 'number' ? {
    mainAxis: rawValue,
    crossAxis: 0,
    alignmentAxis: null
  } : {
    mainAxis: 0,
    crossAxis: 0,
    alignmentAxis: null,
    ...rawValue
  };
  if (alignment && typeof alignmentAxis === 'number') {
    crossAxis = alignment === 'end' ? alignmentAxis * -1 : alignmentAxis;
  }
  return isVertical ? {
    x: crossAxis * crossAxisMulti,
    y: mainAxis * mainAxisMulti
  } : {
    x: mainAxis * mainAxisMulti,
    y: crossAxis * crossAxisMulti
  };
}

/**
 * Modifies the placement by translating the floating element along the
 * specified axes.
 * A number (shorthand for `mainAxis` or distance), or an axes configuration
 * object may be passed.
 * @see https://floating-ui.com/docs/offset
 */
const offset = function (options) {
  if (options === void 0) {
    options = 0;
  }
  return {
    name: 'offset',
    options,
    async fn(state) {
      const {
        x,
        y
      } = state;
      const diffCoords = await convertValueToCoords(state, options);
      return {
        x: x + diffCoords.x,
        y: y + diffCoords.y,
        data: diffCoords
      };
    }
  };
};

/**
 * Provides data that allows you to change the size of the floating element —
 * for instance, prevent it from overflowing the clipping boundary or match the
 * width of the reference element.
 * @see https://floating-ui.com/docs/size
 */
const size = function (options) {
  if (options === void 0) {
    options = {};
  }
  return {
    name: 'size',
    options,
    async fn(state) {
      const {
        placement,
        rects,
        platform,
        elements
      } = state;
      const {
        apply = () => {},
        ...detectOverflowOptions
      } = evaluate(options, state);
      const overflow = await detectOverflow(state, detectOverflowOptions);
      const side = getSide(placement);
      const alignment = getAlignment(placement);
      const isYAxis = getSideAxis(placement) === 'y';
      const {
        width,
        height
      } = rects.floating;
      let heightSide;
      let widthSide;
      if (side === 'top' || side === 'bottom') {
        heightSide = side;
        widthSide = alignment === ((await (platform.isRTL == null ? void 0 : platform.isRTL(elements.floating))) ? 'start' : 'end') ? 'left' : 'right';
      } else {
        widthSide = side;
        heightSide = alignment === 'end' ? 'top' : 'bottom';
      }
      const overflowAvailableHeight = height - overflow[heightSide];
      const overflowAvailableWidth = width - overflow[widthSide];
      const noShift = !state.middlewareData.shift;
      let availableHeight = overflowAvailableHeight;
      let availableWidth = overflowAvailableWidth;
      if (isYAxis) {
        const maximumClippingWidth = width - overflow.left - overflow.right;
        availableWidth = alignment || noShift ? min(overflowAvailableWidth, maximumClippingWidth) : maximumClippingWidth;
      } else {
        const maximumClippingHeight = height - overflow.top - overflow.bottom;
        availableHeight = alignment || noShift ? min(overflowAvailableHeight, maximumClippingHeight) : maximumClippingHeight;
      }
      if (noShift && !alignment) {
        const xMin = max(overflow.left, 0);
        const xMax = max(overflow.right, 0);
        const yMin = max(overflow.top, 0);
        const yMax = max(overflow.bottom, 0);
        if (isYAxis) {
          availableWidth = width - 2 * (xMin !== 0 || xMax !== 0 ? xMin + xMax : max(overflow.left, overflow.right));
        } else {
          availableHeight = height - 2 * (yMin !== 0 || yMax !== 0 ? yMin + yMax : max(overflow.top, overflow.bottom));
        }
      }
      await apply({
        ...state,
        availableWidth,
        availableHeight
      });
      const nextDimensions = await platform.getDimensions(elements.floating);
      if (width !== nextDimensions.width || height !== nextDimensions.height) {
        return {
          reset: {
            rects: true
          }
        };
      }
      return {};
    }
  };
};

function getNodeName(node) {
  if (isNode(node)) {
    return (node.nodeName || '').toLowerCase();
  }
  // Mocked nodes in testing environments may not be instances of Node. By
  // returning `#document` an infinite loop won't occur.
  // https://github.com/floating-ui/floating-ui/issues/2317
  return '#document';
}
function getWindow(node) {
  var _node$ownerDocument;
  return (node == null ? void 0 : (_node$ownerDocument = node.ownerDocument) == null ? void 0 : _node$ownerDocument.defaultView) || window;
}
function getDocumentElement(node) {
  var _ref;
  return (_ref = (isNode(node) ? node.ownerDocument : node.document) || window.document) == null ? void 0 : _ref.documentElement;
}
function isNode(value) {
  return value instanceof Node || value instanceof getWindow(value).Node;
}
function isElement(value) {
  return value instanceof Element || value instanceof getWindow(value).Element;
}
function isHTMLElement(value) {
  return value instanceof HTMLElement || value instanceof getWindow(value).HTMLElement;
}
function isShadowRoot(value) {
  // Browsers without `ShadowRoot` support.
  if (typeof ShadowRoot === 'undefined') {
    return false;
  }
  return value instanceof ShadowRoot || value instanceof getWindow(value).ShadowRoot;
}
function isOverflowElement(element) {
  const {
    overflow,
    overflowX,
    overflowY,
    display
  } = getComputedStyle(element);
  return /auto|scroll|overlay|hidden|clip/.test(overflow + overflowY + overflowX) && !['inline', 'contents'].includes(display);
}
function isTableElement(element) {
  return ['table', 'td', 'th'].includes(getNodeName(element));
}
function isContainingBlock(element) {
  const webkit = isWebKit();
  const css = getComputedStyle(element);

  // https://developer.mozilla.org/en-US/docs/Web/CSS/Containing_block#identifying_the_containing_block
  return css.transform !== 'none' || css.perspective !== 'none' || (css.containerType ? css.containerType !== 'normal' : false) || !webkit && (css.backdropFilter ? css.backdropFilter !== 'none' : false) || !webkit && (css.filter ? css.filter !== 'none' : false) || ['transform', 'perspective', 'filter'].some(value => (css.willChange || '').includes(value)) || ['paint', 'layout', 'strict', 'content'].some(value => (css.contain || '').includes(value));
}
function getContainingBlock(element) {
  let currentNode = getParentNode(element);
  while (isHTMLElement(currentNode) && !isLastTraversableNode(currentNode)) {
    if (isContainingBlock(currentNode)) {
      return currentNode;
    } else {
      currentNode = getParentNode(currentNode);
    }
  }
  return null;
}
function isWebKit() {
  if (typeof CSS === 'undefined' || !CSS.supports) return false;
  return CSS.supports('-webkit-backdrop-filter', 'none');
}
function isLastTraversableNode(node) {
  return ['html', 'body', '#document'].includes(getNodeName(node));
}
function getComputedStyle(element) {
  return getWindow(element).getComputedStyle(element);
}
function getNodeScroll(element) {
  if (isElement(element)) {
    return {
      scrollLeft: element.scrollLeft,
      scrollTop: element.scrollTop
    };
  }
  return {
    scrollLeft: element.pageXOffset,
    scrollTop: element.pageYOffset
  };
}
function getParentNode(node) {
  if (getNodeName(node) === 'html') {
    return node;
  }
  const result =
  // Step into the shadow DOM of the parent of a slotted node.
  node.assignedSlot ||
  // DOM Element detected.
  node.parentNode ||
  // ShadowRoot detected.
  isShadowRoot(node) && node.host ||
  // Fallback.
  getDocumentElement(node);
  return isShadowRoot(result) ? result.host : result;
}
function getNearestOverflowAncestor(node) {
  const parentNode = getParentNode(node);
  if (isLastTraversableNode(parentNode)) {
    return node.ownerDocument ? node.ownerDocument.body : node.body;
  }
  if (isHTMLElement(parentNode) && isOverflowElement(parentNode)) {
    return parentNode;
  }
  return getNearestOverflowAncestor(parentNode);
}
function getOverflowAncestors(node, list) {
  var _node$ownerDocument2;
  if (list === void 0) {
    list = [];
  }
  const scrollableAncestor = getNearestOverflowAncestor(node);
  const isBody = scrollableAncestor === ((_node$ownerDocument2 = node.ownerDocument) == null ? void 0 : _node$ownerDocument2.body);
  const win = getWindow(scrollableAncestor);
  if (isBody) {
    return list.concat(win, win.visualViewport || [], isOverflowElement(scrollableAncestor) ? scrollableAncestor : []);
  }
  return list.concat(scrollableAncestor, getOverflowAncestors(scrollableAncestor));
}

function getCssDimensions(element) {
  const css = getComputedStyle(element);
  // In testing environments, the `width` and `height` properties are empty
  // strings for SVG elements, returning NaN. Fallback to `0` in this case.
  let width = parseFloat(css.width) || 0;
  let height = parseFloat(css.height) || 0;
  const hasOffset = isHTMLElement(element);
  const offsetWidth = hasOffset ? element.offsetWidth : width;
  const offsetHeight = hasOffset ? element.offsetHeight : height;
  const shouldFallback = round(width) !== offsetWidth || round(height) !== offsetHeight;
  if (shouldFallback) {
    width = offsetWidth;
    height = offsetHeight;
  }
  return {
    width,
    height,
    $: shouldFallback
  };
}

function unwrapElement(element) {
  return !isElement(element) ? element.contextElement : element;
}

function getScale(element) {
  const domElement = unwrapElement(element);
  if (!isHTMLElement(domElement)) {
    return createCoords(1);
  }
  const rect = domElement.getBoundingClientRect();
  const {
    width,
    height,
    $
  } = getCssDimensions(domElement);
  let x = ($ ? round(rect.width) : rect.width) / width;
  let y = ($ ? round(rect.height) : rect.height) / height;

  // 0, NaN, or Infinity should always fallback to 1.

  if (!x || !Number.isFinite(x)) {
    x = 1;
  }
  if (!y || !Number.isFinite(y)) {
    y = 1;
  }
  return {
    x,
    y
  };
}

const noOffsets = /*#__PURE__*/createCoords(0);
function getVisualOffsets(element) {
  const win = getWindow(element);
  if (!isWebKit() || !win.visualViewport) {
    return noOffsets;
  }
  return {
    x: win.visualViewport.offsetLeft,
    y: win.visualViewport.offsetTop
  };
}
function shouldAddVisualOffsets(element, isFixed, floatingOffsetParent) {
  if (isFixed === void 0) {
    isFixed = false;
  }
  if (!floatingOffsetParent || isFixed && floatingOffsetParent !== getWindow(element)) {
    return false;
  }
  return isFixed;
}

function getBoundingClientRect(element, includeScale, isFixedStrategy, offsetParent) {
  if (includeScale === void 0) {
    includeScale = false;
  }
  if (isFixedStrategy === void 0) {
    isFixedStrategy = false;
  }
  const clientRect = element.getBoundingClientRect();
  const domElement = unwrapElement(element);
  let scale = createCoords(1);
  if (includeScale) {
    if (offsetParent) {
      if (isElement(offsetParent)) {
        scale = getScale(offsetParent);
      }
    } else {
      scale = getScale(element);
    }
  }
  const visualOffsets = shouldAddVisualOffsets(domElement, isFixedStrategy, offsetParent) ? getVisualOffsets(domElement) : createCoords(0);
  let x = (clientRect.left + visualOffsets.x) / scale.x;
  let y = (clientRect.top + visualOffsets.y) / scale.y;
  let width = clientRect.width / scale.x;
  let height = clientRect.height / scale.y;
  if (domElement) {
    const win = getWindow(domElement);
    const offsetWin = offsetParent && isElement(offsetParent) ? getWindow(offsetParent) : offsetParent;
    let currentIFrame = win.frameElement;
    while (currentIFrame && offsetParent && offsetWin !== win) {
      const iframeScale = getScale(currentIFrame);
      const iframeRect = currentIFrame.getBoundingClientRect();
      const css = getComputedStyle(currentIFrame);
      const left = iframeRect.left + (currentIFrame.clientLeft + parseFloat(css.paddingLeft)) * iframeScale.x;
      const top = iframeRect.top + (currentIFrame.clientTop + parseFloat(css.paddingTop)) * iframeScale.y;
      x *= iframeScale.x;
      y *= iframeScale.y;
      width *= iframeScale.x;
      height *= iframeScale.y;
      x += left;
      y += top;
      currentIFrame = getWindow(currentIFrame).frameElement;
    }
  }
  return rectToClientRect({
    width,
    height,
    x,
    y
  });
}

function convertOffsetParentRelativeRectToViewportRelativeRect(_ref) {
  let {
    rect,
    offsetParent,
    strategy
  } = _ref;
  const isOffsetParentAnElement = isHTMLElement(offsetParent);
  const documentElement = getDocumentElement(offsetParent);
  if (offsetParent === documentElement) {
    return rect;
  }
  let scroll = {
    scrollLeft: 0,
    scrollTop: 0
  };
  let scale = createCoords(1);
  const offsets = createCoords(0);
  if (isOffsetParentAnElement || !isOffsetParentAnElement && strategy !== 'fixed') {
    if (getNodeName(offsetParent) !== 'body' || isOverflowElement(documentElement)) {
      scroll = getNodeScroll(offsetParent);
    }
    if (isHTMLElement(offsetParent)) {
      const offsetRect = getBoundingClientRect(offsetParent);
      scale = getScale(offsetParent);
      offsets.x = offsetRect.x + offsetParent.clientLeft;
      offsets.y = offsetRect.y + offsetParent.clientTop;
    }
  }
  return {
    width: rect.width * scale.x,
    height: rect.height * scale.y,
    x: rect.x * scale.x - scroll.scrollLeft * scale.x + offsets.x,
    y: rect.y * scale.y - scroll.scrollTop * scale.y + offsets.y
  };
}

function getClientRects(element) {
  return Array.from(element.getClientRects());
}

function getWindowScrollBarX(element) {
  // If <html> has a CSS width greater than the viewport, then this will be
  // incorrect for RTL.
  return getBoundingClientRect(getDocumentElement(element)).left + getNodeScroll(element).scrollLeft;
}

// Gets the entire size of the scrollable document area, even extending outside
// of the `<html>` and `<body>` rect bounds if horizontally scrollable.
function getDocumentRect(element) {
  const html = getDocumentElement(element);
  const scroll = getNodeScroll(element);
  const body = element.ownerDocument.body;
  const width = max(html.scrollWidth, html.clientWidth, body.scrollWidth, body.clientWidth);
  const height = max(html.scrollHeight, html.clientHeight, body.scrollHeight, body.clientHeight);
  let x = -scroll.scrollLeft + getWindowScrollBarX(element);
  const y = -scroll.scrollTop;
  if (getComputedStyle(body).direction === 'rtl') {
    x += max(html.clientWidth, body.clientWidth) - width;
  }
  return {
    width,
    height,
    x,
    y
  };
}

function getViewportRect(element, strategy) {
  const win = getWindow(element);
  const html = getDocumentElement(element);
  const visualViewport = win.visualViewport;
  let width = html.clientWidth;
  let height = html.clientHeight;
  let x = 0;
  let y = 0;
  if (visualViewport) {
    width = visualViewport.width;
    height = visualViewport.height;
    const visualViewportBased = isWebKit();
    if (!visualViewportBased || visualViewportBased && strategy === 'fixed') {
      x = visualViewport.offsetLeft;
      y = visualViewport.offsetTop;
    }
  }
  return {
    width,
    height,
    x,
    y
  };
}

// Returns the inner client rect, subtracting scrollbars if present.
function getInnerBoundingClientRect(element, strategy) {
  const clientRect = getBoundingClientRect(element, true, strategy === 'fixed');
  const top = clientRect.top + element.clientTop;
  const left = clientRect.left + element.clientLeft;
  const scale = isHTMLElement(element) ? getScale(element) : createCoords(1);
  const width = element.clientWidth * scale.x;
  const height = element.clientHeight * scale.y;
  const x = left * scale.x;
  const y = top * scale.y;
  return {
    width,
    height,
    x,
    y
  };
}
function getClientRectFromClippingAncestor(element, clippingAncestor, strategy) {
  let rect;
  if (clippingAncestor === 'viewport') {
    rect = getViewportRect(element, strategy);
  } else if (clippingAncestor === 'document') {
    rect = getDocumentRect(getDocumentElement(element));
  } else if (isElement(clippingAncestor)) {
    rect = getInnerBoundingClientRect(clippingAncestor, strategy);
  } else {
    const visualOffsets = getVisualOffsets(element);
    rect = {
      ...clippingAncestor,
      x: clippingAncestor.x - visualOffsets.x,
      y: clippingAncestor.y - visualOffsets.y
    };
  }
  return rectToClientRect(rect);
}
function hasFixedPositionAncestor(element, stopNode) {
  const parentNode = getParentNode(element);
  if (parentNode === stopNode || !isElement(parentNode) || isLastTraversableNode(parentNode)) {
    return false;
  }
  return getComputedStyle(parentNode).position === 'fixed' || hasFixedPositionAncestor(parentNode, stopNode);
}

// A "clipping ancestor" is an `overflow` element with the characteristic of
// clipping (or hiding) child elements. This returns all clipping ancestors
// of the given element up the tree.
function getClippingElementAncestors(element, cache) {
  const cachedResult = cache.get(element);
  if (cachedResult) {
    return cachedResult;
  }
  let result = getOverflowAncestors(element).filter(el => isElement(el) && getNodeName(el) !== 'body');
  let currentContainingBlockComputedStyle = null;
  const elementIsFixed = getComputedStyle(element).position === 'fixed';
  let currentNode = elementIsFixed ? getParentNode(element) : element;

  // https://developer.mozilla.org/en-US/docs/Web/CSS/Containing_block#identifying_the_containing_block
  while (isElement(currentNode) && !isLastTraversableNode(currentNode)) {
    const computedStyle = getComputedStyle(currentNode);
    const currentNodeIsContaining = isContainingBlock(currentNode);
    if (!currentNodeIsContaining && computedStyle.position === 'fixed') {
      currentContainingBlockComputedStyle = null;
    }
    const shouldDropCurrentNode = elementIsFixed ? !currentNodeIsContaining && !currentContainingBlockComputedStyle : !currentNodeIsContaining && computedStyle.position === 'static' && !!currentContainingBlockComputedStyle && ['absolute', 'fixed'].includes(currentContainingBlockComputedStyle.position) || isOverflowElement(currentNode) && !currentNodeIsContaining && hasFixedPositionAncestor(element, currentNode);
    if (shouldDropCurrentNode) {
      // Drop non-containing blocks.
      result = result.filter(ancestor => ancestor !== currentNode);
    } else {
      // Record last containing block for next iteration.
      currentContainingBlockComputedStyle = computedStyle;
    }
    currentNode = getParentNode(currentNode);
  }
  cache.set(element, result);
  return result;
}

// Gets the maximum area that the element is visible in due to any number of
// clipping ancestors.
function getClippingRect(_ref) {
  let {
    element,
    boundary,
    rootBoundary,
    strategy
  } = _ref;
  const elementClippingAncestors = boundary === 'clippingAncestors' ? getClippingElementAncestors(element, this._c) : [].concat(boundary);
  const clippingAncestors = [...elementClippingAncestors, rootBoundary];
  const firstClippingAncestor = clippingAncestors[0];
  const clippingRect = clippingAncestors.reduce((accRect, clippingAncestor) => {
    const rect = getClientRectFromClippingAncestor(element, clippingAncestor, strategy);
    accRect.top = max(rect.top, accRect.top);
    accRect.right = min(rect.right, accRect.right);
    accRect.bottom = min(rect.bottom, accRect.bottom);
    accRect.left = max(rect.left, accRect.left);
    return accRect;
  }, getClientRectFromClippingAncestor(element, firstClippingAncestor, strategy));
  return {
    width: clippingRect.right - clippingRect.left,
    height: clippingRect.bottom - clippingRect.top,
    x: clippingRect.left,
    y: clippingRect.top
  };
}

function getDimensions(element) {
  return getCssDimensions(element);
}

function getRectRelativeToOffsetParent(element, offsetParent, strategy) {
  const isOffsetParentAnElement = isHTMLElement(offsetParent);
  const documentElement = getDocumentElement(offsetParent);
  const isFixed = strategy === 'fixed';
  const rect = getBoundingClientRect(element, true, isFixed, offsetParent);
  let scroll = {
    scrollLeft: 0,
    scrollTop: 0
  };
  const offsets = createCoords(0);
  if (isOffsetParentAnElement || !isOffsetParentAnElement && !isFixed) {
    if (getNodeName(offsetParent) !== 'body' || isOverflowElement(documentElement)) {
      scroll = getNodeScroll(offsetParent);
    }
    if (isOffsetParentAnElement) {
      const offsetRect = getBoundingClientRect(offsetParent, true, isFixed, offsetParent);
      offsets.x = offsetRect.x + offsetParent.clientLeft;
      offsets.y = offsetRect.y + offsetParent.clientTop;
    } else if (documentElement) {
      offsets.x = getWindowScrollBarX(documentElement);
    }
  }
  return {
    x: rect.left + scroll.scrollLeft - offsets.x,
    y: rect.top + scroll.scrollTop - offsets.y,
    width: rect.width,
    height: rect.height
  };
}

function getTrueOffsetParent(element, polyfill) {
  if (!isHTMLElement(element) || getComputedStyle(element).position === 'fixed') {
    return null;
  }
  if (polyfill) {
    return polyfill(element);
  }
  return element.offsetParent;
}

// Gets the closest ancestor positioned element. Handles some edge cases,
// such as table ancestors and cross browser bugs.
function getOffsetParent(element, polyfill) {
  const window = getWindow(element);
  if (!isHTMLElement(element)) {
    return window;
  }
  let offsetParent = getTrueOffsetParent(element, polyfill);
  while (offsetParent && isTableElement(offsetParent) && getComputedStyle(offsetParent).position === 'static') {
    offsetParent = getTrueOffsetParent(offsetParent, polyfill);
  }
  if (offsetParent && (getNodeName(offsetParent) === 'html' || getNodeName(offsetParent) === 'body' && getComputedStyle(offsetParent).position === 'static' && !isContainingBlock(offsetParent))) {
    return window;
  }
  return offsetParent || getContainingBlock(element) || window;
}

const getElementRects = async function (_ref) {
  let {
    reference,
    floating,
    strategy
  } = _ref;
  const getOffsetParentFn = this.getOffsetParent || getOffsetParent;
  const getDimensionsFn = this.getDimensions;
  return {
    reference: getRectRelativeToOffsetParent(reference, await getOffsetParentFn(floating), strategy),
    floating: {
      x: 0,
      y: 0,
      ...(await getDimensionsFn(floating))
    }
  };
};

function isRTL(element) {
  return getComputedStyle(element).direction === 'rtl';
}

const platform = {
  convertOffsetParentRelativeRectToViewportRelativeRect,
  getDocumentElement,
  getClippingRect,
  getOffsetParent,
  getElementRects,
  getClientRects,
  getDimensions,
  getScale,
  isElement,
  isRTL
};

// https://samthor.au/2021/observing-dom/
function observeMove(element, onMove) {
  let io = null;
  let timeoutId;
  const root = getDocumentElement(element);
  function cleanup() {
    clearTimeout(timeoutId);
    io && io.disconnect();
    io = null;
  }
  function refresh(skip, threshold) {
    if (skip === void 0) {
      skip = false;
    }
    if (threshold === void 0) {
      threshold = 1;
    }
    cleanup();
    const {
      left,
      top,
      width,
      height
    } = element.getBoundingClientRect();
    if (!skip) {
      onMove();
    }
    if (!width || !height) {
      return;
    }
    const insetTop = floor(top);
    const insetRight = floor(root.clientWidth - (left + width));
    const insetBottom = floor(root.clientHeight - (top + height));
    const insetLeft = floor(left);
    const rootMargin = -insetTop + "px " + -insetRight + "px " + -insetBottom + "px " + -insetLeft + "px";
    const options = {
      rootMargin,
      threshold: max(0, min(1, threshold)) || 1
    };
    let isFirstUpdate = true;
    function handleObserve(entries) {
      const ratio = entries[0].intersectionRatio;
      if (ratio !== threshold) {
        if (!isFirstUpdate) {
          return refresh();
        }
        if (!ratio) {
          timeoutId = setTimeout(() => {
            refresh(false, 1e-7);
          }, 100);
        } else {
          refresh(false, ratio);
        }
      }
      isFirstUpdate = false;
    }

    // Older browsers don't support a `document` as the root and will throw an
    // error.
    try {
      io = new IntersectionObserver(handleObserve, {
        ...options,
        // Handle <iframe>s
        root: root.ownerDocument
      });
    } catch (e) {
      io = new IntersectionObserver(handleObserve, options);
    }
    io.observe(element);
  }
  refresh(true);
  return cleanup;
}

/**
 * Automatically updates the position of the floating element when necessary.
 * Should only be called when the floating element is mounted on the DOM or
 * visible on the screen.
 * @returns cleanup function that should be invoked when the floating element is
 * removed from the DOM or hidden from the screen.
 * @see https://floating-ui.com/docs/autoUpdate
 */
function autoUpdate(reference, floating, update, options) {
  if (options === void 0) {
    options = {};
  }
  const {
    ancestorScroll = true,
    ancestorResize = true,
    elementResize = typeof ResizeObserver === 'function',
    layoutShift = typeof IntersectionObserver === 'function',
    animationFrame = false
  } = options;
  const referenceEl = unwrapElement(reference);
  const ancestors = ancestorScroll || ancestorResize ? [...(referenceEl ? getOverflowAncestors(referenceEl) : []), ...getOverflowAncestors(floating)] : [];
  ancestors.forEach(ancestor => {
    ancestorScroll && ancestor.addEventListener('scroll', update, {
      passive: true
    });
    ancestorResize && ancestor.addEventListener('resize', update);
  });
  const cleanupIo = referenceEl && layoutShift ? observeMove(referenceEl, update) : null;
  let reobserveFrame = -1;
  let resizeObserver = null;
  if (elementResize) {
    resizeObserver = new ResizeObserver(_ref => {
      let [firstEntry] = _ref;
      if (firstEntry && firstEntry.target === referenceEl && resizeObserver) {
        // Prevent update loops when using the `size` middleware.
        // https://github.com/floating-ui/floating-ui/issues/1740
        resizeObserver.unobserve(floating);
        cancelAnimationFrame(reobserveFrame);
        reobserveFrame = requestAnimationFrame(() => {
          resizeObserver && resizeObserver.observe(floating);
        });
      }
      update();
    });
    if (referenceEl && !animationFrame) {
      resizeObserver.observe(referenceEl);
    }
    resizeObserver.observe(floating);
  }
  let frameId;
  let prevRefRect = animationFrame ? getBoundingClientRect(reference) : null;
  if (animationFrame) {
    frameLoop();
  }
  function frameLoop() {
    const nextRefRect = getBoundingClientRect(reference);
    if (prevRefRect && (nextRefRect.x !== prevRefRect.x || nextRefRect.y !== prevRefRect.y || nextRefRect.width !== prevRefRect.width || nextRefRect.height !== prevRefRect.height)) {
      update();
    }
    prevRefRect = nextRefRect;
    frameId = requestAnimationFrame(frameLoop);
  }
  update();
  return () => {
    ancestors.forEach(ancestor => {
      ancestorScroll && ancestor.removeEventListener('scroll', update);
      ancestorResize && ancestor.removeEventListener('resize', update);
    });
    cleanupIo && cleanupIo();
    resizeObserver && resizeObserver.disconnect();
    resizeObserver = null;
    if (animationFrame) {
      cancelAnimationFrame(frameId);
    }
  };
}

/**
 * Computes the `x` and `y` coordinates that will place the floating element
 * next to a reference element when it is given a certain CSS positioning
 * strategy.
 */
const computePosition = (reference, floating, options) => {
  // This caches the expensive `getClippingElementAncestors` function so that
  // multiple lifecycle resets re-use the same result. It only lives for a
  // single call. If other functions become expensive, we can add them as well.
  const cache = new Map();
  const mergedOptions = {
    platform,
    ...options
  };
  const platformWithCache = {
    ...mergedOptions.platform,
    _c: cache
  };
  return computePosition$1(reference, floating, {
    ...mergedOptions,
    platform: platformWithCache
  });
};

// Unique ID creation requires a high quality random # generator. In the browser we therefore
// require the crypto API and do not support built-in fallback to lower quality random number
// generators (like Math.random()).
var getRandomValues;
var rnds8 = new Uint8Array(16);
function rng() {
  // lazy load so that environments that need to polyfill have a chance to do so
  if (!getRandomValues) {
    // getRandomValues needs to be invoked in a context where "this" is a Crypto implementation. Also,
    // find the complete implementation of crypto (msCrypto) on IE11.
    getRandomValues = typeof crypto !== 'undefined' && crypto.getRandomValues && crypto.getRandomValues.bind(crypto) || typeof msCrypto !== 'undefined' && typeof msCrypto.getRandomValues === 'function' && msCrypto.getRandomValues.bind(msCrypto);

    if (!getRandomValues) {
      throw new Error('crypto.getRandomValues() not supported. See https://github.com/uuidjs/uuid#getrandomvalues-not-supported');
    }
  }

  return getRandomValues(rnds8);
}

const REGEX = /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}|00000000-0000-0000-0000-000000000000)$/i;

function validate(uuid) {
  return typeof uuid === 'string' && REGEX.test(uuid);
}

/**
 * Convert array of 16 byte values to UUID string format of the form:
 * XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX
 */

var byteToHex = [];

for (var i = 0; i < 256; ++i) {
  byteToHex.push((i + 0x100).toString(16).substr(1));
}

function stringify(arr) {
  var offset = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  // Note: Be careful editing this code!  It's been tuned for performance
  // and works in ways you may not expect. See https://github.com/uuidjs/uuid/pull/434
  var uuid = (byteToHex[arr[offset + 0]] + byteToHex[arr[offset + 1]] + byteToHex[arr[offset + 2]] + byteToHex[arr[offset + 3]] + '-' + byteToHex[arr[offset + 4]] + byteToHex[arr[offset + 5]] + '-' + byteToHex[arr[offset + 6]] + byteToHex[arr[offset + 7]] + '-' + byteToHex[arr[offset + 8]] + byteToHex[arr[offset + 9]] + '-' + byteToHex[arr[offset + 10]] + byteToHex[arr[offset + 11]] + byteToHex[arr[offset + 12]] + byteToHex[arr[offset + 13]] + byteToHex[arr[offset + 14]] + byteToHex[arr[offset + 15]]).toLowerCase(); // Consistency check for valid UUID.  If this throws, it's likely due to one
  // of the following:
  // - One or more input array values don't map to a hex octet (leading to
  // "undefined" in the uuid)
  // - Invalid input values for the RFC `version` or `variant` fields

  if (!validate(uuid)) {
    throw TypeError('Stringified UUID is invalid');
  }

  return uuid;
}

function v4(options, buf, offset) {
  options = options || {};
  var rnds = options.random || (options.rng || rng)(); // Per 4.4, set bits for version and `clock_seq_hi_and_reserved`

  rnds[6] = rnds[6] & 0x0f | 0x40;
  rnds[8] = rnds[8] & 0x3f | 0x80; // Copy bytes to buffer, if provided

  if (buf) {
    offset = offset || 0;

    for (var i = 0; i < 16; ++i) {
      buf[offset + i] = rnds[i];
    }

    return buf;
  }

  return stringify(rnds);
}

/**
 * helper functions for managing callback timing (ex. delay, throttle, debounce)
 *
 * these will probably be most useful in situations where we wish to respond to
 * user input events (particularly ones that happen in "streams" like scrolling,
 * window resizing, or typing) in a performant way.
 *
 * if this file ends up larger than a few functions, we should consider bringing
 * in lodash as a dependency instead: https://lodash.com/docs
 *
 * recommended pattern for using these functions in your stencil components:
 * myEventHandler = debounce(() => {
 *   // your code
 * }, timeout)
 */
/**
 * returns a function that will not be triggered until it stops being called for
 * N milliseconds, adapted from https://davidwalsh.name/javascript-debounce-function
 *
 * @param callback - callback to be executed after the wait
 * @param {number} wait - wait in milliseconds
 */
const debounce = (callback, wait) => {
  let timeout;
  return (...args) => {
    const later = () => {
      timeout = null;
      callback.apply(undefined, args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const marketSelectCss = ":host,::slotted(*),*{box-sizing:border-box}:host([disabled]){cursor:not-allowed !important;}:host([disabled]) *,:host([disabled]) ::slotted(*){cursor:not-allowed !important;pointer-events:none !important;}:host{position:relative;outline:none;font-weight:var(--field-input-weight);font-size:var(--field-input-size);line-height:var(--field-input-leading);cursor:text}:host input,:host ::slotted(input),:host textarea,:host ::slotted(textarea){width:100%;margin:0;padding:0;border:none;background-color:transparent;color:inherit;font-weight:inherit;font-size:inherit;font-family:inherit;line-height:inherit;letter-spacing:inherit;cursor:inherit}:host ::slotted(label){cursor:inherit}:host input:focus,:host ::slotted(input:focus),:host textarea:focus,:host ::slotted(textarea:focus){outline:none}:host input::-moz-placeholder,:host textarea::-moz-placeholder{color:var(--field-placeholder-text-color)}:host input::placeholder,:host textarea::placeholder{color:var(--field-placeholder-text-color)}:host ::slotted(input)::-moz-placeholder,:host ::slotted(textarea)::-moz-placeholder{color:var(--field-placeholder-text-color)}:host ::slotted(input)::placeholder,:host ::slotted(textarea)::placeholder{color:var(--field-placeholder-text-color)}:host([size='small']){font-size:var(--core-type-paragraph-20-size);line-height:var(--core-type-paragraph-20-leading)}:host{border-radius:var(--field-border-radius);background-color:var(--field-normal-state-background-color);color:var(--field-normal-state-input-color);box-shadow:inset 0 0 0 var(--field-border-size) var(--field-normal-state-normal-validity-border-color)}:host([invalid]){box-shadow:inset 0 0 0 var(--field-border-size) var(--field-normal-state-invalid-validity-border-color)}:host(:hover){background-color:var(--field-hover-state-background-color);color:var(--field-hover-state-input-color);box-shadow:inset 0 0 0 var(--field-border-size) var(--field-hover-state-normal-validity-border-color)}:host([invalid]:hover){box-shadow:inset 0 0 0 var(--field-border-size) var(--field-hover-state-invalid-validity-border-color)}:host([focused]){color:var(--field-focus-state-input-color);box-shadow:inset 0 0 0 var(--field-focus-state-border-size) var(--field-focus-state-normal-validity-border-color)}:host([focused][invalid]){box-shadow:inset 0 0 0 var(--field-focus-state-border-size) var(--field-focus-state-invalid-validity-border-color)}:host([disabled]){background-color:var(--field-disabled-state-background-color) !important;color:var(--field-disabled-state-input-color) !important;box-shadow:inset 0 0 0 var(--field-border-size) var(--field-disabled-state-border-color) !important;cursor:not-allowed !important;}:host([value=\"\"]) ::slotted(label){color:var(--field-normal-state-empty-phase-label-color)}:host(:not([value=\"\"])) ::slotted(label),:host([value=\"\"][autofilled]) ::slotted(label){color:var(--field-normal-state-float-phase-label-color)}:host(:hover) ::slotted(label){color:var(--field-hover-state-empty-phase-label-color)}:host(:not([value=\"\"]):hover) ::slotted(label),:host([value=\"\"][autofilled]:hover) ::slotted(label){color:var(--field-hover-state-float-phase-label-color)}:host([focused]) ::slotted(label){color:var(--field-focus-state-float-phase-label-color)}:host([value=\"\"][disabled]) ::slotted(label){color:var(--field-disabled-state-empty-phase-label-color)}:host(:not([value=\"\"])[disabled]) ::slotted(label),:host([value=\"\"][autofilled][disabled]) ::slotted(label){color:var(--field-disabled-state-float-phase-label-color)}:host{--field-accessory-horizontal-spacing-size:16px;--field-size-small-accessory-horizontal-spacing-size:12px;--field-size-large-image-accessory-outer-spacing-size:12px;--field-size-medium-image-accessory-outer-spacing-size:4px;--field-size-large-button-accessory-outer-spacing-size:12px;--field-size-medium-button-accessory-outer-spacing-size:4px;--field-size-large-tooltip-accessory-horizontal-spacing-size:5px;--field-size-large-tooltip-accessory-vertical-spacing-size:-1px;--field-size-small-tooltip-accessory-horizontal-spacing-size:1px}:host ::slotted([slot='leading-accessory']){flex-shrink:0;margin-right:var(--field-accessory-horizontal-spacing-size);margin-left:var(--field-accessory-horizontal-spacing-size)}:host ::slotted(.market-accessory[slot='leading-accessory'][size='image']){margin-left:var(--field-size-large-image-accessory-outer-spacing-size)}:host ::slotted(.market-tooltip[slot='leading-accessory']){margin:var(--field-size-large-tooltip-accessory-vertical-spacing-size)\n      var(--field-size-large-tooltip-accessory-horizontal-spacing-size)}:host ::slotted(.market-button[slot='leading-accessory'][size='small']){margin-left:var(--field-size-large-button-accessory-outer-spacing-size)}:host ::slotted([slot='trailing-accessory']){flex-shrink:0;margin-right:var(--field-accessory-horizontal-spacing-size);margin-left:var(--field-accessory-horizontal-spacing-size)}:host ::slotted(.market-accessory[slot='trailing-accessory'][size='image']){margin-right:var(--field-size-large-image-accessory-outer-spacing-size)}:host ::slotted(.market-tooltip[slot='trailing-accessory']){margin:var(--field-size-large-tooltip-accessory-vertical-spacing-size)\n      var(--field-size-large-tooltip-accessory-horizontal-spacing-size)}:host ::slotted(.market-button[slot='trailing-accessory'][size='small']){margin-right:var(--field-size-large-button-accessory-outer-spacing-size)}:host([size='medium']) ::slotted(.market-accessory[slot='leading-accessory'][size='image']){margin-left:var(--field-size-medium-image-accessory-outer-spacing-size)}:host([size='medium']) ::slotted(.market-button[slot='leading-accessory'][size='small']){margin-left:var(--field-size-medium-button-accessory-outer-spacing-size)}:host([size='medium']) ::slotted(.market-accessory[slot='trailing-accessory'][size='image']){margin-right:var(--field-size-medium-image-accessory-outer-spacing-size)}:host([size='medium']) ::slotted(.market-button[slot='trailing-accessory'][size='small']){margin-right:var(--field-size-medium-button-accessory-outer-spacing-size)}:host([size='small']) ::slotted([slot='leading-accessory']),:host([size='small']) ::slotted([slot='trailing-accessory']){margin-right:var(--field-size-small-accessory-horizontal-spacing-size);margin-left:var(--field-size-small-accessory-horizontal-spacing-size)}:host([size='small']) ::slotted(.market-tooltip[slot='leading-accessory']),:host([size='small']) ::slotted(.market-tooltip[slot='trailing-accessory']){margin-right:var(--field-size-small-tooltip-accessory-horizontal-spacing-size);margin-left:var(--field-size-small-tooltip-accessory-horizontal-spacing-size)}:host .label-input-container.has-leading-accessory{padding-left:0 !important;}:host .label-input-container.has-trailing-accessory{padding-right:0 !important;}:host{--field-input-animation-speed:0.2s;--field-input-label-translate:12px;--field-empty-phase-label-text-size-unitless:16;--field-float-phase-label-text-size-unitless:14;--field-size-medium-float-phase-vertical-padding-size:12px;--field-size-medium-float-phase-horizontal-padding-size:16px;--field-size-small-float-phase-vertical-padding-size:9px;--field-size-small-float-phase-horizontal-padding-size:12px;display:flex;align-items:center}:host .label-input-container,:host([value='']:not([focused]):not([autofilled])) .label-input-container{flex-grow:1;padding:var(--field-float-phase-vertical-padding-size) var(--field-float-phase-horizontal-padding-size)}:host([size='medium']) .label-input-container,:host([size='medium'][value='']:not([focused]):not([autofilled])) .label-input-container{padding:var(--field-size-medium-float-phase-vertical-padding-size)\n      var(--field-size-medium-float-phase-horizontal-padding-size)}:host([size='small']) .label-input-container,:host([size='small'][value='']:not([focused]):not([autofilled])) .label-input-container{padding:var(--field-size-small-float-phase-vertical-padding-size)\n      var(--field-size-small-float-phase-horizontal-padding-size)}:host ::slotted(label){display:block;min-height:var(--field-empty-phase-label-text-leading);font-weight:var(--field-empty-phase-label-text-weight);font-size:var(--field-empty-phase-label-text-size);line-height:var(--field-empty-phase-label-text-leading);letter-spacing:var(--field-empty-phase-label-text-tracking);transition:all var(--field-input-animation-speed);transform:translateY(var(--field-input-label-translate));transform-origin:0 0}:host(:not([value=\"\"])) ::slotted(label),:host([value=\"\"]:not([focused])[autofilled]) ::slotted(label),:host([focused]) ::slotted(label){font-weight:var(--field-float-phase-label-text-weight);transform:scale(\n        calc(var(--field-float-phase-label-text-size-unitless) / var(--field-empty-phase-label-text-size-unitless))\n      )}:host input,:host ::slotted(input),:host textarea,:host ::slotted(textarea){opacity:0}:host([focused]) input,:host([focused]) ::slotted(input),:host([focused]) textarea,:host([focused]) ::slotted(textarea),:host(:not([value=''])) input,:host(:not([value=''])) ::slotted(input),:host(:not([value=''])) textarea,:host(:not([value=''])) ::slotted(textarea){opacity:1}:host([size='small']) ::slotted(label),:host([size='medium']) ::slotted(label){position:absolute;overflow:hidden;clip:rect(0 0 0 0);width:1px;height:1px;white-space:nowrap;clip-path:inset(50%)}:host([size='small']) input,:host([size='small']) ::slotted(input),:host([size='small']) textarea,:host([size='small']) ::slotted(textarea),:host([size='medium']) input,:host([size='medium']) ::slotted(input),:host([size='medium']) textarea,:host([size='medium']) ::slotted(textarea){opacity:1}:host{cursor:pointer}.label-input-container{overflow:hidden}.caret{flex-shrink:0;margin-right:var(--field-float-phase-horizontal-padding-size);pointer-events:none;transition:transform var(--field-input-animation-speed)}.caret path{fill:var(--select-caret-color)}:host([aria-expanded='true']) .caret{transform:rotate(180deg)}.placeholder{overflow:hidden;height:var(--field-input-leading);color:var(--field-placeholder-text-color);font-size:var(--field-input-size);line-height:var(--field-input-leading);text-overflow:ellipsis;white-space:nowrap;visibility:hidden}:host([focused]) .placeholder,:host([size='small']) .placeholder,:host([size='medium']) .placeholder{visibility:visible}:host([size='small']) .placeholder{height:var(--core-type-paragraph-20-leading);font-size:var(--core-type-paragraph-20-size);line-height:var(--core-type-paragraph-20-leading)}::slotted(label){cursor:pointer}::slotted([slot='displayed-selection']){min-height:0;margin:0}::slotted([slot='displayed-selection'])::before,::slotted([slot='displayed-selection'])::after{display:none}::slotted([slot='list']){display:none}";

const MarketSelect = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
    this.marketSelectValueDidChange = createEvent(this, "marketSelectValueDidChange", 7);
    this.marketSelectOpened = createEvent(this, "marketSelectOpened", 7);
    this.marketSelectClosed = createEvent(this, "marketSelectClosed", 7);
    this.hasLeadingAccessory = false;
    this.hasTrailingAccessory = false;
    /* TYPEAHEAD FUNCTIONALITY
    - https://www.w3.org/TR/wai-aria-practices/examples/listbox/listbox-collapsible.html
    - type a character: focus moves to the next item with a name that starts with
      the typed character
    - type multiple characters in rapid succession: focus moves to the next item
      with a name that starts with the string of characters typed
    - if no match is found, focus remains where it was
    - search wraps around end of list
    */
    this.rowsInnerText = [];
    this.keypresses = [];
    this.debounceDelay = 250;
    this.setFocusOnMatch = debounce(() => {
      const searchString = this.keypresses.join('');
      // array slicing is so we start the search from the currently focused row
      const nextMatch = [
        ...this.rowsInnerText.slice(this.focusedRowIndex + 1),
        ...this.rowsInnerText.slice(0, this.focusedRowIndex + 1),
      ].find((innerText) => innerText.startsWith(searchString));
      const matchIndex = this.rowsInnerText.findIndex((innerText) => {
        return innerText.startsWith(nextMatch);
      });
      const matchingRow = this.rows[matchIndex];
      // if matching row exists and is not disabled, focus it
      if (matchingRow && !matchingRow.disabled) {
        this.focusedRowIndex = matchIndex;
        matchingRow.focus();
        // if list is closed and single select, set the value
        if (!this.listIsActive && !this.multiselect) {
          // row is not in the DOM, so matchingRow.click() doesn't bubble
          // instead we manually set the value to the row's value
          this.value = matchingRow.value;
          // since we're bypassing marketListSelectionsDidChange event here,
          // we also need to manually emit marketSelectValueDidChange event
          this.marketSelectValueDidChange.emit({
            value: this.value,
            newSelectedOption: matchingRow,
            newDeselectedOption: null,
            currentSelectedOptions: [matchingRow],
          });
        }
      }
      this.keypresses = []; // reset keypress array
    }, this.debounceDelay);
    this.list = null;
    this.rows = null;
    this.popoverElement = null;
    this.name = undefined;
    this.value = '';
    this.size = 'large';
    this.placeholder = undefined;
    this.readonly = false;
    this.disabled = false;
    this.focused = false;
    this.invalid = false;
    this.multiselect = false;
    this.popoverContainer = undefined;
    this.listIsActive = false;
    this.focusedRowIndex = -1;
  }
  valueWatcher() {
    this.propagateValue();
  }
  multiselectWatcher() {
    this.list.multiselect = this.multiselect;
  }
  // Listener for the change event emitted by `market-list`
  handleListSelection({ detail: { currentSelectionValues, currentSelections, newDeselection, newSelection } }) {
    const value = currentSelectionValues.join(',');
    this.value = value;
    if (this.multiselect) {
      this.setFocusedRow(newDeselection || newSelection);
    }
    else {
      this.setFocusedRow(newSelection);
    }
    this.marketSelectValueDidChange.emit({
      value,
      newSelectedOption: newSelection,
      newDeselectedOption: newDeselection,
      currentSelectedOptions: currentSelections,
    });
  }
  handleListItemsFiltered() {
    window.requestAnimationFrame(() => {
      if (this.listIsActive) {
        this.updatePopper();
      }
    });
  }
  windowClick(e) {
    var _a;
    if (this.el.contains(e.target) || ((_a = this.popoverElement) === null || _a === void 0 ? void 0 : _a.contains(e.target))) {
      return;
    }
    this.focused = false;
    if (this.listIsActive) {
      this.closeList();
    }
  }
  getValues() {
    if (Array.isArray(this.value)) {
      return new Set(this.value);
    }
    return new Set(this.multiselect ? this.value.split(',') : [this.value]);
  }
  getValuesCount() {
    return this.getValues().size;
  }
  /**
   * We search based on the `value` property rather than using querySelector and the `value`
   * attribute (i.e. `market-row[value=${this.value}]`) due to rendering issues we've seen
   * with Ember/Handlebars, where the property may be set before attribute is present.
   * See here for details: https://github.com/squareup/market/issues/2635
   */
  getMatchingRows() {
    const values = this.getValues();
    return [...this.rows].filter((row) => values.has(row.value));
  }
  get hasMultipleSelections() {
    return this.multiselect && this.getValues().size > 1;
  }
  getMultiselectDisplayValue() {
    const values = this.getValues();
    return values.size > 1 ? `${values.size} selected` : '';
  }
  getDisplayedSelectionRow() {
    return this.el.querySelector(`${getNamespacedTagFor('market-row')}[slot="displayed-selection"]`);
  }
  /**
   * Ensure the passed `value` is reflected in the selected list item and the displayed selection row.
   */
  propagateValue() {
    if (!this.list || !this.rows)
      return;
    const matchingRows = this.getMatchingRows();
    if (matchingRows.length === 0) {
      // there were no matching rows, so sanitize & reset the value
      this.list.value = this.value = '';
      this.focusedRowIndex = -1;
    }
    else {
      // set the list value so the correct list row will be selected
      this.list.value = this.value;
      this.setFocusedRow(matchingRows[0]);
    }
    this.displaySelection(matchingRows);
  }
  /**
   * Renders the passed row into the displayed selection slot, so it's visible
   * as the current selection in the main "input" area of the select.
   */
  displaySelection(selectedRows = []) {
    var _a, _b, _c;
    // Remove existing selected row from the DOM
    (_b = (_a = this.getDisplayedSelectionRow()) === null || _a === void 0 ? void 0 : _a.remove) === null || _b === void 0 ? void 0 : _b.call(_a);
    if (selectedRows.length === 1) {
      /**
       * For single select, we need to clone the row from the list so we can place a
       * duplicate into the field area. If we don't clone it, the row
       * will be removed from the list when we append it to the selection area
       */
      const clonedRow = selectedRows[0].cloneNode(true);
      // Row was cloned from the list, so we need to remove its interactive attribute
      clonedRow.interactive = false;
      /**
       * Set slot='displayed-selection' so this shows up within the slotted area.
       * We use a slot instead of appending this directly into the shadowDOM
       * because we want to keep whatever styling cascaded from the Light DOM
       * on the row because market - row can accept any markup.
       */
      clonedRow.setAttribute('slot', 'displayed-selection');
      // if the select size is small, the selected row needs to be small too
      clonedRow.size = this.size === 'small' ? 'small' : 'medium';
      // Append the cloned node after the first slot element (label).
      this.el.append(clonedRow);
    }
    else if (selectedRows.length > 1) {
      /**
       * For multiselect, we need to create a `market-row` element to display "n selected"
       * and possibly clone the "selected-translation" slot, if provided
       */
      const multiselectDisplayRow = document.createElement(`${getNamespacedTagFor('market-row')}`);
      multiselectDisplayRow.setAttribute('slot', 'displayed-selection');
      multiselectDisplayRow.innerText = `${this.getValuesCount().toLocaleString()} `;
      // if the select size is small, the selected row needs to be small too
      multiselectDisplayRow.size = this.size === 'small' ? 'small' : 'medium';
      // Add slot for "selected"
      const selectedTranslationSlot = document.createElement('slot');
      selectedTranslationSlot.setAttribute('name', 'selected-translation');
      // Clone the contents of the "selected-translation"
      const selectedTranslation = this.el.querySelector('[slot="selected-translation"]');
      selectedTranslationSlot.innerHTML = (_c = selectedTranslation === null || selectedTranslation === void 0 ? void 0 : selectedTranslation.innerHTML) !== null && _c !== void 0 ? _c : 'selected';
      multiselectDisplayRow.appendChild(selectedTranslationSlot);
      // Observe for changes on the "selected-translation" slot
      if (selectedTranslation) {
        const observer = new MutationObserver(() => {
          selectedTranslationSlot.innerHTML = selectedTranslation === null || selectedTranslation === void 0 ? void 0 : selectedTranslation.innerHTML;
        });
        observer.observe(selectedTranslation, { characterData: true, subtree: true });
      }
      this.el.append(multiselectDisplayRow);
    }
  }
  /**
   * Listens to changes in list content to ensure that if the content is dynamically updated,
   * those changes will be copied to the popover and the displayed-selection row.
   */
  initListObserver() {
    const observer = new MutationObserver(this.onListChange.bind(this));
    observer.observe(this.list, { childList: true, characterData: true, subtree: true });
  }
  onListChange() {
    this.initRows();
    this.propagateValue();
    this.updatePopper();
  }
  /**
   * Record the index of the new selected row for keyboard navigation
   */
  setFocusedRow(row) {
    this.focusedRowIndex = Array.prototype.indexOf.call(this.rows, row);
  }
  /**
   * Opens the select.
   */
  async openList() {
    if (this.listIsActive || !this.popoverElement) {
      return Promise.resolve();
    }
    const container = this.el.closest(this.popoverContainer) || document.body;
    Object.assign(this.popoverElement.style, {
      zIndex: `${getMaxZIndex(container) + 1}`,
      visibility: 'hidden',
    });
    this.moveListToPopover();
    container.append(this.popoverElement);
    this.initPopperListeners();
    return new Promise((resolve) => {
      // hack: ensure initial position is correctly set before visible
      setTimeout(() => {
        this.updatePopper();
        this.popoverElement.style.visibility = 'visible';
        this.listIsActive = true;
        this.marketSelectOpened.emit();
        resolve();
      }, 50);
    });
  }
  /**
   * Closes the select.
   */
  closeList() {
    if (!this.listIsActive) {
      return Promise.resolve();
    }
    this.popoverElement.remove();
    this.cleanupPopperListeners();
    this.moveListToSelect();
    this.listIsActive = false;
    this.marketSelectClosed.emit();
    return Promise.resolve();
  }
  /**
   * Toggles the select open and closed.
   */
  async toggleList() {
    this.listIsActive ? await this.closeList() : await this.openList();
  }
  /* KEYBOARD ACCESSIBILITY */
  // keyboard handler on the trigger (separate from the list)
  handleTriggerKeyDown(ev) {
    switch (ev.key) {
      case 'ArrowDown':
        this.handleTriggerArrowDown(ev);
        break;
      case 'ArrowUp':
        this.handleTriggerArrowUp(ev);
        break;
      case ' ':
        this.handleTriggerSpacebar(ev);
        break;
      case 'Enter':
        this.handleTriggerEnter();
        break;
      case 'Escape':
        this.handleEscape();
        break;
      case 'Tab':
        this.handleTab(ev);
        break;
      default:
        this.typeaheadHandler(ev);
        break;
    }
    ev.stopPropagation();
  }
  /**
   * NOTE: market-list has it's own internal keyboard functionality,
   * so we only need to handle the trigger keypress here
   *
   * - if the list is open:
   *   - focus the next enabled row
   * - if the list is closed:
   *   - open the list
   *   - if there is a current selection, focus it
   */
  async handleTriggerArrowDown(ev) {
    ev.preventDefault(); // do not scroll page while select has focus
    if (this.listIsActive) {
      for (let i = this.focusedRowIndex + 1; i < this.rows.length; i++) {
        if (!this.rows[i].disabled) {
          this.focusedRowIndex = i;
          this.rows[i].focus();
          break;
        }
      }
    }
    else {
      await this.openList();
      if (this.focusedRowIndex > -1) {
        this.rows[this.focusedRowIndex].focus();
      }
    }
  }
  /**
   * NOTE: market-list has it's own internal keyboard functionality,
   * so we only need to handle the trigger keypress here
   *
   * - if the list is open:
   *   - focus the last enabled row
   * - if the list is closed:
   *   - open the list
   *   - if there is a current selection, focus it
   */
  async handleTriggerArrowUp(ev) {
    ev.preventDefault(); // do not scroll page while select has focus
    if (this.listIsActive) {
      for (let i = this.focusedRowIndex - 1; i > -1; i--) {
        if (!this.rows[i].disabled) {
          this.focusedRowIndex = i;
          this.rows[i].focus();
          break;
        }
      }
    }
    else {
      await this.openList();
      if (this.focusedRowIndex > -1) {
        this.rows[this.focusedRowIndex].focus();
      }
    }
  }
  /**
   * - if the list is open:
   *   - close it
   * - if the list is closed:
   *   - open the list
   *   - if there is a current selection, focus it
   */
  async handleTriggerSpacebar(ev) {
    ev.preventDefault(); // do not scroll page while select has focus
    if (this.listIsActive) {
      this.closeList();
    }
    else {
      await this.openList();
      if (this.focusedRowIndex > -1) {
        this.rows[this.focusedRowIndex].focus();
      }
    }
  }
  /**
   * when the list is closed, invoke implicit submission
   */
  handleTriggerEnter() {
    if (this.listIsActive) {
      this.closeList();
      this.el.focus();
    }
    else {
      submitFormImplicitly(this.el);
    }
  }
  /*
  - when the list is open, close it and focus the trigger
  */
  handleEscape() {
    if (this.listIsActive) {
      this.closeList();
      this.el.focus();
    }
  }
  /*
  - when the list is open, suppress tab navigation
  - when the list is closed, allow tab navigation and remove focus
  */
  handleTab(ev) {
    if (this.listIsActive) {
      ev.preventDefault();
    }
    else {
      this.focused = false;
    }
  }
  // keyboard handler on the list (separate from the trigger)
  handleListKeyDown(ev) {
    switch (ev.key) {
      case 'Enter':
        this.handleListEnter();
        break;
      case 'Escape':
        this.handleEscape();
        break;
      case 'Tab':
        this.handleTab(ev);
        break;
      case ' ':
        this.handleListSpacebar(ev);
        break;
      default:
        this.typeaheadHandler(ev);
        break;
    }
    ev.stopPropagation();
  }
  /**
   * market-list handles selection functionality internally
   * just need to close the list if single select
   */
  handleListSpacebar(ev) {
    // do nothing if list is multiselect or keydown event is emitted by typing into a slotted search input
    if (this.multiselect || ev.target.hasAttribute('slot', 'search')) {
      return;
    }
    ev.preventDefault(); // do not scroll page while select has focus
    this.closeList();
    this.el.focus();
  }
  /**
   * market-list handles selection functionality internally
   * just need to close the list if single select
   */
  handleListEnter() {
    if (!this.multiselect) {
      this.closeList();
      this.el.focus();
    }
  }
  typeaheadHandler(ev) {
    var _a;
    // if list has a search, that will be used instead of a typeahead
    if ((_a = this.list) === null || _a === void 0 ? void 0 : _a.hasAttribute('has-search')) {
      return;
    }
    // because this is the default keydown event handler, we're ignoring
    // "special" keys, numbers, and punctuation for typeahead functionality
    // note that this check will also ignore characters from languages that have
    // no concept of upper/lowercase (ex. japanese)
    // TODO: revisit if localization requires it
    if (ev.key.length > 1 || ev.key.toUpperCase() === ev.key.toLowerCase()) {
      return;
    }
    this.storeKeypresses(ev);
    this.setFocusOnMatch();
  }
  storeKeypresses(ev) {
    this.keypresses = [...this.keypresses, ev.key];
  }
  /* EVENT HANDLING */
  handleFocus() {
    if (this.readonly || this.disabled) {
      return;
    }
    this.focused = true;
  }
  handleTriggerClick(ev) {
    if (ev.target.tagName.toLowerCase() === getNamespacedTagFor('market-tooltip')) {
      return;
    }
    if (this.readonly || this.disabled) {
      return;
    }
    this.toggleList();
  }
  handleListClick() {
    if (!this.multiselect) {
      this.closeList();
      this.el.focus();
    }
  }
  initPopperListeners() {
    const { el, popoverElement, updatePopper } = this;
    this.cleanupPopperListeners = autoUpdate(el, popoverElement, updatePopper.bind(this), {
      elementResize: false,
    });
  }
  updatePopper() {
    const { el, popoverElement } = this;
    // TODO: use design tokens
    const MARGIN_OFFSET = 8;
    const MAX_HEIGHT = 464;
    computePosition(el, popoverElement, {
      middleware: [
        offset(MARGIN_OFFSET),
        autoPlacement({
          allowedPlacements: ['top', 'bottom'],
        }),
        size({
          apply({ rects, availableHeight }) {
            Object.assign(popoverElement.style, {
              width: `${rects.reference.width}px`,
              maxHeight: `${Math.min(availableHeight - MARGIN_OFFSET, MAX_HEIGHT)}px`,
            });
          },
        }),
      ],
    }).then(({ x, y }) => {
      Object.assign(popoverElement.style, {
        left: `${x}px`,
        top: `${y}px`,
      });
    });
  }
  initPopover() {
    this.popoverElement =
      this.popoverElement ||
        document.createElement(getNamespacedTagFor('market-popover'));
    this.popoverElement.id = this.popoverId;
    this.popoverElement.style.position = 'absolute';
  }
  initList() {
    this.list = this.el.querySelector('[slot="list"]');
    this.list.interactive = true;
    this.list.multiselect = this.multiselect;
    this.list.addEventListener('marketListSelectionsDidChange', this.handleListSelection.bind(this));
    this.list.addEventListener('marketListItemsFiltered', this.handleListItemsFiltered.bind(this));
    this.list.addEventListener('keydown', this.handleListKeyDown.bind(this));
    this.list.addEventListener('click', this.handleListClick.bind(this));
  }
  initRows() {
    this.rows = this.list.querySelectorAll(`${getNamespacedTagFor('market-row')}`);
    // used for typeahead functionality
    this.rowsInnerText = [...this.rows]
      .filter((row) => {
      /**
       * Addresses an issue in test environments where the innerText of
       * market-rows isn't set before componentWillLoad of the parent market-select (this file)
       * is executed.
       */
      return row && row.innerText;
    })
      .map((row) => row.innerText.trim().toLowerCase());
  }
  moveListToPopover() {
    this.list.removeAttribute('slot');
    this.popoverElement.append(this.list);
  }
  moveListToSelect() {
    this.list.setAttribute('slot', 'list');
    this.el.append(this.list);
  }
  /* COMPONENT LIFECYCLE EVENTS */
  componentWillLoad() {
    const leadingAccessory = [...this.el.children].filter(function (child) {
      return child.matches('[slot="leading-accessory"]');
    });
    const trailingAccessory = [...this.el.children].filter(function (child) {
      return child.matches('[slot="trailing-accessory"]');
    });
    this.hasLeadingAccessory = leadingAccessory.length > 0;
    this.hasTrailingAccessory = trailingAccessory.length > 0;
    this.popoverId = this.popoverId || `popover-${v4()}`;
  }
  componentDidLoad() {
    this.initList();
    this.initRows();
    this.initPopover();
    this.initListObserver();
    this.propagateValue();
  }
  disconnectedCallback() {
    this.closeList();
  }
  render() {
    const tabindex = this.disabled ? null : '0';
    return (h(Host, { class: "market-select", role: "combobox", "aria-label": getTextInputAriaLabel(this.el), "aria-expanded": this.listIsActive.toString(), "aria-controls": this.popoverId, "aria-haspopup": "listbox", tabindex: tabindex, onClick: (e) => {
        this.handleTriggerClick(e);
      }, onFocus: () => {
        this.handleFocus();
      }, onKeyDown: (e) => {
        this.handleTriggerKeyDown(e);
      } }, h("slot", { name: "leading-accessory" }), h("div", { class: classNames('label-input-container', {
        'has-leading-accessory': this.hasLeadingAccessory,
        'has-trailing-accessory': this.hasTrailingAccessory,
      }) }, h("slot", null), h("slot", { name: "displayed-selection" }, h("div", { class: "placeholder" }, this.placeholder))), h("slot", { name: "list" }), h("slot", { name: "trailing-accessory" }), h("svg", { class: "caret", width: "14", height: "8", viewBox: "0 0 14 8", xmlns: "http://www.w3.org/2000/svg" }, h("path", { "fill-rule": "evenodd", "clip-rule": "evenodd", d: "M7.70715 7.70711C7.31663 8.09763 6.68346 8.09763 6.29294 7.70711L0.29294 1.70711L1.70715 0.292892L7.00005 5.58579L12.2929 0.292893L13.7072 1.70711L7.70715 7.70711Z", fill: "black" }))));
  }
  get el() { return getElement(this); }
  static get watchers() { return {
    "value": ["valueWatcher"],
    "multiselect": ["multiselectWatcher"]
  }; }
};
MarketSelect.style = marketSelectCss;

export { MarketSelect as market_select };
